/**
 * Jira Integration API client.
 *
 * TYPE SYNCHRONIZATION NOTE:
 * The TypeScript interfaces in this file correspond to Pydantic models in:
 *   apps/api/src/models/jira_models.py
 *
 * Synced types (update BOTH files when modifying):
 *   - CloneToProjectRequest     ↔ CloneToProjectRequest
 *   - CloneToProjectResponse    ↔ CloneToProjectResponse
 *   - JiraCompleteConnectionResponse ↔ JiraCompleteConnectionResponse
 *   - JiraConnectResponse       ↔ JiraConnectResponse
 *   - JiraIssue                 ↔ JiraIssue
 *   - JiraIssuesResponse        ↔ JiraIssuesResponse
 *   - JiraStatusResponse        ↔ JiraStatusResponse
 *
 * Frontend-only types (no backend equivalent):
 *   - JiraIssuesParams (query parameter wrapper)
 *   - JiraSite (multi-site selection UI helper)
 *
 * The backend Pydantic models are the source of truth. See:
 *   docs/backend/FRONTEND_BACKEND_TYPES.md
 */

import { apiRequest } from './client';
import { ENDPOINTS, HTTP_METHODS } from './config';

/**
 * Request for POST /api/integrations/jira/clone-to-project
 */
export interface CloneToProjectRequest {
  issue_key: string;
  summary: string;
  description?: string | null;
  status?: string | null;
  priority?: string | null;
  labels?: string[];
  comments?: JiraComment[];
}

/**
 * Response from POST /api/integrations/jira/clone-to-project
 */
export interface CloneToProjectResponse {
  project_id: string;
  project_title: string;
  prd_session_id: string | null;
  redirect_url: string;
  used_fallback: boolean;
}

/**
 * Single Jira comment.
 * Type sync: apps/api/src/models/jira_models.py - JiraComment class
 */
export interface JiraComment {
  author: string;
  body: string;
  created: string;
}

/**
 * Response from POST /api/integrations/jira/complete-connection
 */
export interface JiraCompleteConnectionResponse {
  success: boolean;
  site_url: string;
}

/**
 * Response from POST /api/integrations/jira/connect
 */
export interface JiraConnectResponse {
  redirect_url: string;
}

/**
 * Single Jira issue in the ticket list.
 * Type sync: apps/api/src/models/jira_models.py - JiraIssue class
 */
export interface JiraIssue {
  key: string;
  summary: string;
  issue_type: string;
  status: string;
  priority: string;
  updated: string;
  url: string;
  description: string | null;
  labels: string[];
  comments: JiraComment[];
  linked_project_id: string | null;
}

/**
 * Parameters for getIssues API call.
 */
export interface JiraIssuesParams {
  page?: number;
  limit?: number;
  sortBy?: 'priority' | 'updated';
  nextPageToken?: string;
}

/**
 * Paginated list of Jira issues.
 */
export interface JiraIssuesResponse {
  items: JiraIssue[];
  total: number;
  page: number;
  limit: number;
  next_page_token: string | null;
}

/**
 * Site info for multi-site selection
 */
export interface JiraSite {
  id: string;
  name: string;
  url: string;
}

/**
 * Response from GET /api/integrations/jira/status
 */
export interface JiraStatusResponse {
  connected: boolean;
  site_url: string | null;
  needs_reconnect: boolean;
}

/**
 * Disconnect Jira integration for current user.
 */
export async function deleteJiraConnection(): Promise<void> {
  await apiRequest(`${ENDPOINTS.JIRA}/disconnect`, {
    method: HTTP_METHODS.DELETE,
  });
}

/**
 * Get Jira issues assigned to the current user.
 *
 * @param params - Pagination and sort parameters
 * @returns Paginated list of issues
 */
export async function getJiraIssues(params: JiraIssuesParams = {}): Promise<JiraIssuesResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.sortBy) searchParams.set('sort_by', params.sortBy);
  if (params.nextPageToken) searchParams.set('next_page_token', params.nextPageToken);

  const queryString = searchParams.toString();
  const url = `${ENDPOINTS.JIRA}/issues${queryString ? `?${queryString}` : ''}`;

  const response = await apiRequest<JiraIssuesResponse>(url);
  if (!response.data) {
    throw new Error('Failed to get Jira issues: no data returned');
  }
  return response.data;
}

/**
 * Get Jira connection status for current user.
 *
 * @returns Status response with connected flag and site URL
 */
export async function getJiraStatus(): Promise<JiraStatusResponse> {
  const response = await apiRequest<JiraStatusResponse>(`${ENDPOINTS.JIRA}/status`);
  if (!response.data) {
    throw new Error('Failed to get Jira status: no data returned');
  }
  return response.data;
}

/**
 * Clone a Jira issue to a new project.
 *
 * Creates a new project from the Jira issue data with LLM-generated
 * title and description. Automatically starts a PRD session.
 *
 * @param request - Issue data to clone
 * @returns Clone response with project_id and redirect_url
 */
export async function postCloneToProject(
  request: CloneToProjectRequest,
): Promise<CloneToProjectResponse> {
  const response = await apiRequest<CloneToProjectResponse>(`${ENDPOINTS.JIRA}/clone-to-project`, {
    method: HTTP_METHODS.POST,
    body: JSON.stringify(request),
    timeout: 45000, // 45s: 15s agent LLM + 15s PRD session + 15s buffer
  });
  if (!response.data) {
    throw new Error('Failed to clone Jira issue: no data returned');
  }
  return response.data;
}

/**
 * Complete Jira connection after multi-site selection.
 *
 * @param pendingState - Pending state token from callback
 * @param siteId - Selected Jira cloud ID
 * @returns Complete connection response
 */
export async function postJiraCompleteConnection(
  pendingState: string,
  siteId: string,
): Promise<JiraCompleteConnectionResponse> {
  const response = await apiRequest<JiraCompleteConnectionResponse>(
    `${ENDPOINTS.JIRA}/complete-connection`,
    {
      method: HTTP_METHODS.POST,
      body: JSON.stringify({ pending_state: pendingState, site_id: siteId }),
    },
  );
  if (!response.data) {
    throw new Error('Failed to complete Jira connection: no data returned');
  }
  return response.data;
}

/**
 * Initiate Jira OAuth connection flow.
 * Returns a redirect URL to Atlassian's authorization page.
 *
 * @returns Connect response with redirect URL
 */
export async function postJiraConnect(): Promise<JiraConnectResponse> {
  const response = await apiRequest<JiraConnectResponse>(`${ENDPOINTS.JIRA}/connect`, {
    method: HTTP_METHODS.POST,
  });
  if (!response.data) {
    throw new Error('Failed to initiate Jira connection: no data returned');
  }
  return response.data;
}
